import { API_BASE_URL } from '../config/api';
import { setServerTime } from '../lib/serverClock';
import { authFetch, getAccessToken } from './session';

/**
 * SSE-over-fetch client for `GET /api/live/events` (see
 * notes/live-chat-contract-2026-09-30.md, section 1).
 *
 * `EventSource` cannot send the Bearer header, so the stream is read with
 * `fetch` + `ReadableStream` like `assistantService.ts`. Every (re)connect
 * goes through the session, so the stream outlives its access token. The
 * frame parser is exported on its own and is pure, so it can be unit-tested
 * without a browser.
 */

export const LIVE_EVENTS_URL = `${API_BASE_URL}/live/events`;

/** One parsed `text/event-stream` frame, before the JSON payload is decoded. */
export interface SseFrame {
  /** Last `id:` seen on the stream (it persists across frames per the SSE spec). */
  id: string | null;
  /** `event:` field; `message` when the frame carries none. */
  event: string;
  /** All `data:` lines of the frame joined with `\n`. */
  data: string;
}

/** A decoded event from the backend broker. */
export interface LiveEvent<T = unknown> {
  id: number | null;
  type: string;
  data: T;
}

/**
 * `stopped` = the client gave up for good (no token / HTTP 401): nothing is
 * open, nothing is retried, and polling would only hit the same 401.
 */
export type LiveStreamStatus = 'connecting' | 'open' | 'degraded' | 'stopped';

export interface SseParser {
  /** Feed a raw text chunk. Chunks may split lines and frames anywhere. */
  push: (chunk: string) => void;
  /** Stream ended: parse whatever is still buffered and emit a pending frame. */
  flush: () => void;
}

/**
 * Incremental SSE frame parser per the WHATWG spec: `id:`/`event:`/`data:`
 * fields, a blank line ends a frame, `:`-lines are comments (the backend's
 * `: ping` heartbeat), multi-line `data:` is joined with `\n`, one optional
 * space after the colon is stripped, CRLF / LF / CR line endings all work.
 */
export function createSseParser(onFrame: (frame: SseFrame) => void): SseParser {
  let buffer = '';
  let eventName = '';
  let dataLines: string[] = [];
  let lastId: string | null = null;

  const dispatch = () => {
    // A frame without any data line is dropped (spec), but the id still sticks.
    if (dataLines.length > 0) {
      onFrame({ id: lastId, event: eventName || 'message', data: dataLines.join('\n') });
    }
    eventName = '';
    dataLines = [];
  };

  const handleLine = (line: string) => {
    if (line === '') {
      dispatch();
      return;
    }
    if (line.startsWith(':')) return; // comment / heartbeat
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    switch (field) {
      case 'event':
        eventName = value;
        break;
      case 'data':
        dataLines.push(value);
        break;
      case 'id':
        if (!value.includes('\u0000')) lastId = value;
        break;
      default:
        // `retry:` and unknown fields are ignored.
        break;
    }
  };

  const push = (chunk: string) => {
    buffer += chunk;
    let pos = 0;
    for (;;) {
      const cr = buffer.indexOf('\r', pos);
      const lf = buffer.indexOf('\n', pos);
      if (cr === -1 && lf === -1) break;
      let end: number;
      let next: number;
      if (cr !== -1 && (lf === -1 || cr < lf)) {
        // A trailing CR may be the first half of a CRLF split across chunks.
        if (cr === buffer.length - 1) break;
        end = cr;
        next = buffer[cr + 1] === '\n' ? cr + 2 : cr + 1;
      } else {
        end = lf;
        next = lf + 1;
      }
      handleLine(buffer.slice(pos, end));
      pos = next;
    }
    buffer = buffer.slice(pos);
  };

  const flush = () => {
    const rest = buffer;
    buffer = '';
    if (rest) handleLine(rest.replace(/[\r\n]+$/, ''));
    dispatch();
  };

  return { push, flush };
}

/** Convenience for tests and one-shot bodies: parse a complete text into frames. */
export function parseSseText(text: string): SseFrame[] {
  const frames: SseFrame[] = [];
  const parser = createSseParser((frame) => frames.push(frame));
  parser.push(text);
  parser.flush();
  return frames;
}

/** Decode a frame's numeric id and JSON payload; `null` when the payload is not JSON. */
export function frameToLiveEvent(frame: SseFrame): LiveEvent | null {
  let data: unknown;
  try {
    data = JSON.parse(frame.data);
  } catch {
    return null;
  }
  const parsedId = frame.id == null ? NaN : Number(frame.id);
  return { id: Number.isFinite(parsedId) ? parsedId : null, type: frame.event, data };
}

export const BACKOFF_MIN_MS = 1_000;
export const BACKOFF_MAX_MS = 30_000;
/** After this many consecutive failed attempts the caller should poll instead. */
export const DEGRADED_AFTER_FAILURES = 2;
/**
 * No byte for this long = the stream is dead (half-open TCP, proxy that
 * swallowed the close). The backend sends `: ping` every 20 s, so 45 s is
 * two missed heartbeats plus slack.
 */
export const STREAM_IDLE_TIMEOUT_MS = 45_000;
/**
 * Only a connection that stayed open this long resets the failure counter.
 * A server that accepts and closes right away would otherwise be retried
 * every second forever instead of backing off (and degrading to polling).
 */
export const STABLE_CONNECTION_MS = 10_000;

/**
 * Exponential backoff 1 s → 30 s with ±30 % jitter so many tabs do not
 * reconnect in lockstep after a backend restart. `random` is a test seam.
 */
export function backoffDelay(failures: number, random: number = Math.random()): number {
  const exponent = Math.max(0, failures - 1);
  const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** exponent);
  const jittered = base * (0.7 + 0.6 * random);
  return Math.round(Math.min(BACKOFF_MAX_MS, jittered));
}

export interface ConnectOptions {
  /** Resume from this event id (`?after=`); replays buffered events with a higher id. */
  after?: number | null;
  onEvent: (event: LiveEvent) => void;
  onStatus?: (status: LiveStreamStatus) => void;
  /**
   * Test seams. The defaults are the session's own: a token renewed when it is
   * about to expire, and a fetch whose 401 is final (the session is over and
   * the app has been told).
   */
  fetchImpl?: typeof fetch;
  getToken?: () => string | null | Promise<string | null>;
  url?: string;
}

type AttemptOutcome = 'stop' | 'closed' | 'failed';

/**
 * Open the stream and keep it open: reconnects with backoff, resumes from the
 * last seen id, reports `degraded` after two consecutive failures (the caller
 * then polls) while still retrying in the background, and `stopped` when it
 * gives up (no token / 401). A stream that stays silent for
 * STREAM_IDLE_TIMEOUT_MS is aborted and reconnected. Returns a disconnect
 * function; nothing is reported after it was called.
 */
export function connect(options: ConnectOptions): () => void {
  const fetchImpl: typeof fetch = options.fetchImpl ?? authFetch;
  const getToken = options.getToken ?? getAccessToken;
  const url = options.url ?? LIVE_EVENTS_URL;

  let lastId: number | null = options.after ?? null;
  let failures = 0;
  let stopped = false;
  let controller: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let wake: (() => void) | null = null;
  /** When the current attempt received its first event (null = not open yet). */
  let openedAt: number | null = null;

  const setStatus = (status: LiveStreamStatus) => {
    if (!stopped) options.onStatus?.(status);
  };

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const finish = () => {
        if (timer) clearTimeout(timer);
        timer = null;
        wake = null;
        resolve();
      };
      wake = finish;
      timer = setTimeout(finish, ms);
    });

  // Coming back online / to the tab is the moment a retry is most likely to
  // succeed (and background tabs get their timers throttled anyway).
  const onOnline = () => wake?.();
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') wake?.();
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisibilityChange);

  const streamOnce = async (): Promise<AttemptOutcome> => {
    // Throws while the server cannot be reached to renew the token: that is a
    // failed attempt like any other and is retried with backoff.
    const token = await getToken();
    if (!token) return 'stop';
    // Disconnected while the token was being renewed: nothing would ever close
    // a stream opened now.
    if (stopped) return 'stop';

    const attempt = new AbortController();
    controller = attempt;
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let watchdog: ReturnType<typeof setTimeout> | null = null;
    let stalled = false;
    // Re-armed on every raw chunk BEFORE parsing, so the `: ping` heartbeat
    // (a comment the parser drops) keeps a quiet but healthy stream alive.
    // Armed before the request too: a connect that never answers is dead as well.
    const armWatchdog = () => {
      if (watchdog) clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        stalled = true;
        attempt.abort();
        // Belt and braces: not every fetch implementation errors the body
        // stream on abort; cancelling the reader always ends a pending read().
        reader?.cancel().catch(() => undefined);
      }, STREAM_IDLE_TIMEOUT_MS);
    };

    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
    };
    if (lastId != null) headers['Last-Event-ID'] = String(lastId);
    const query = lastId != null ? `?after=${encodeURIComponent(String(lastId))}` : '';

    try {
      armWatchdog();
      const response = await fetchImpl(`${url}${query}`, {
        method: 'GET',
        headers,
        cache: 'no-store',
        signal: attempt.signal,
      });

      if (response.status === 401) {
        // Final: authFetch already tried to renew the session and told the app.
        return 'stop';
      }
      if (!response.ok || !response.body) {
        throw new Error(`Live stream failed (HTTP ${response.status})`);
      }

      const parser = createSseParser((frame) => {
        const event = frameToLiveEvent(frame);
        if (!event) return;
        if (openedAt === null) {
          openedAt = Date.now();
          setStatus('open');
        }
        if (event.id != null) lastId = event.id;
        if (event.type === 'ready') {
          // Calibrate the server clock (transfer banner ages/expiry use it).
          setServerTime((event.data as { server_time?: unknown } | null)?.server_time);
          const serverLastId = (event.data as { last_id?: unknown } | null)?.last_id;
          if (typeof serverLastId === 'number') {
            // Fresh connection: start from the server's cursor. A cursor that is
            // now BEHIND ours means the backend restarted (ids are per process);
            // keeping the old one would filter out every future event.
            if (lastId == null || serverLastId < lastId) lastId = serverLastId;
          }
        }
        if (!stopped) options.onEvent(event);
      });

      const body = response.body.getReader();
      reader = body;
      const decoder = new TextDecoder();
      armWatchdog();
      for (;;) {
        const { done, value } = await body.read();
        if (done) break;
        armWatchdog();
        parser.push(decoder.decode(value, { stream: true }));
      }
      if (stalled) throw new Error('stalled'); // cancelled by the watchdog, not closed by the server
      parser.push(decoder.decode());
      parser.flush();
      return openedAt !== null ? 'closed' : 'failed';
    } catch (error) {
      if (stalled && !stopped) {
        throw new Error(`Live stream stalled: no data for ${STREAM_IDLE_TIMEOUT_MS / 1000} s`);
      }
      throw error;
    } finally {
      if (watchdog) clearTimeout(watchdog);
    }
  };

  (async () => {
    setStatus('connecting');
    while (!stopped) {
      openedAt = null;
      let outcome: AttemptOutcome;
      try {
        outcome = await streamOnce();
      } catch (error) {
        outcome = 'failed';
        if (!stopped) console.warn('Live events stream error:', error);
      }
      if (stopped) break;
      if (outcome === 'stop') {
        // Nothing is open and nothing will retry: never leave 'open'/'connecting' behind.
        setStatus('stopped');
        break;
      }
      // Only a connection that proved itself healthy wipes the failure history.
      if (openedAt !== null && Date.now() - openedAt >= STABLE_CONNECTION_MS) failures = 0;
      failures += 1;
      setStatus(failures >= DEGRADED_AFTER_FAILURES ? 'degraded' : 'connecting');
      await sleep(backoffDelay(failures));
    }
  })();

  return () => {
    stopped = true;
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    controller?.abort();
    wake?.();
  };
}

const liveEventsService = { connect, createSseParser, parseSseText, frameToLiveEvent, backoffDelay };

export default liveEventsService;
